import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { TextField, FormControlLabel, Switch, Grid, MenuItem } from '@mui/material';
import { Livestream, Discord } from './LivestreamApi';
import { useTranslation } from 'react-i18next';
import Button from 'components/CustomButtons/Button.js';

const useStyles = makeStyles<Theme>((theme) => ({
  form: {
    width: '100%',
    margin: '0 auto',
    textAlign: 'left',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: '100%',
    textAlign: 'left',
  },
  button: {
    width: '100%',
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    textAlign: 'left',
  },
  description: {
    minHeight: '200px',
  },
  switchContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  },
}));

interface LivestreamFormProps {
  livestream?: Livestream;
  discords: Discord[];
  onSubmit: (livestream: Livestream) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const LivestreamForm: React.FC<LivestreamFormProps> = ({
  livestream,
  discords,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Livestream>({
    name: '',
    url: '',
    description: '',
    published: false,
    discord_id: '',
  });

  useEffect(() => {
    if (livestream) {
      setFormData({
        ...livestream,
      });
    }
  }, [livestream]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(formData);
    },
    [formData, onSubmit]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleSwitchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
    }));
  }, []);

  return (
    <>
      <form onSubmit={handleSubmit} style={{ marginTop: '12px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label={t('livestreams.form.nameLabel')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('livestreams.form.linkLabel')}
              name="url"
              value={formData.url}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
              placeholder="https://www.twitch.tv/example"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('livestreams.form.descriptionLabel')}
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label={t('livestreams.form.discordLabel')}
              name="discord_id"
              value={formData.discord_id}
              onChange={handleChange}
              fullWidth
              disabled={isLoading}
              SelectProps={{
                MenuProps: {
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                },
              }}
              InputProps={{
                style: { textAlign: 'left' },
              }}
              sx={{
                '& .MuiSelect-select': {
                  textAlign: 'left',
                },
              }}
            >
              {discords.map((discord) => (
                <MenuItem
                  key={discord.id}
                  value={discord.id}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  {discord.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.switchContainer}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleSwitchChange}
                    name="published"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={t('livestreams.form.publishedLabel')}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" color="primary" disabled={isLoading} fullWidth={true}>
              {livestream && livestream.id ? t('common.update') : t('common.create')}
            </Button>
            <Button color="outlined" onClick={onCancel} fullWidth={true} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export { LivestreamForm };
